/**
 * InstallPrompt — PWA install prompt for iOS and Android.
 *
 * - Android Chrome: captures beforeinstallprompt event
 * - iOS Safari: shows manual "Add to Home Screen" instructions
 * - Only shows once per session (dismissible)
 */
import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as unknown as { standalone: boolean }).standalone === true)
  );
}

const DISMISS_KEY = "copilot-install-dismissed";

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Capture Android install prompt
  useEffect(() => {
    if (isStandalone() || dismissed) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [dismissed]);

  // Check for iOS Safari
  useEffect(() => {
    if (isStandalone() || dismissed) return;
    if (isIOS()) {
      // Only show after a brief delay so it doesn't block initial load
      const timer = setTimeout(() => setShowIOSPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [dismissed]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // sessionStorage not available
    }
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
    dismiss();
  }, [deferredPrompt, dismiss]);

  // Don't show if already installed or dismissed
  if (dismissed || isStandalone()) return null;

  // Android Chrome install prompt
  if (deferredPrompt) {
    return (
      <div className="fixed inset-x-4 bottom-20 z-50 rounded-xl border border-border bg-surface-elevated p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50">
            <img
              src="/copilot-icons/icon-192.png"
              alt=""
              className="h-6 w-6 rounded"
            />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-primary">
              Install Sales Copilot
            </p>
            <p className="mt-0.5 text-xs text-text-secondary">
              Add to your home screen for quick access and offline support
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-text-muted"
            aria-label="Dismiss"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleInstallClick}
            className="flex-1 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white active:bg-brand-600"
          >
            Install
          </button>
          <button
            onClick={dismiss}
            className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary active:bg-surface-muted"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari instructions
  if (showIOSPrompt) {
    return (
      <div className="fixed inset-x-4 bottom-20 z-50 rounded-xl border border-border bg-surface-elevated p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50">
            <img
              src="/copilot-icons/icon-192.png"
              alt=""
              className="h-6 w-6 rounded"
            />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-primary">
              Add to Home Screen
            </p>
            <p className="mt-1 text-xs text-text-secondary leading-relaxed">
              Tap the{" "}
              <svg className="inline h-4 w-4 align-text-bottom text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>{" "}
              share button, then scroll down and tap{" "}
              <strong>&ldquo;Add to Home Screen&rdquo;</strong>
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-text-muted"
            aria-label="Dismiss"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return null;
}
